// src/App.js

import React, { useState, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PortalSessionProvider, BookingDraftProvider } from './context/PortalContext';
import PrivateRoute from './routes/PrivateRoute';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import StripePaymentPage from './pages/StripePaymentPage';
import StripeSuccessPage from './pages/StripeSuccessPage';
import SetPasswordPage from './pages/SetPasswordPage';
import PublicBookingLinkPage from './pages/PublicBookingLinkPage';
import ManageBookingPage from './pages/ManageBookingPage';
import ResendBookingLinkPage from './pages/ResendBookingLinkPage';
import AdminWrapper from './pages/admin/AdminWrapper';
import SuperadminWrapper from './pages/superadmin/SuperadminWrapper';
import PortalWrapper from './pages/portal/PortalWrapper';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import { Toaster } from 'react-hot-toast';
import CrispChat from './components/support/CrispChat';
import CookieConsentManager from './components/privacy/CookieConsentManager';
import { COOKIE_CATEGORIES } from './constants/cookieConsent';
import { getDefaultConsentPreferences } from './utils/cookieConsent';

const DashboardRedirect = () => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <div className="text-center mt-20 text-xl">Loading...</div>;
  if (!user) return <Navigate to="/?auth=1" replace />;
  if (user.role === 'superadmin') return <Navigate to="/superadmin" replace />;
  if (user.role === 'shop_admin' || user.role === 'barber') return <Navigate to="/admin" replace />;
  return <Navigate to="/?auth=1" replace />;
};

const App = () => {
  const [cookiePreferences, setCookiePreferences] = useState(getDefaultConsentPreferences);
  const canLoadTracking = Boolean(cookiePreferences[COOKIE_CATEGORIES.ANALYTICS]) || Boolean(cookiePreferences[COOKIE_CATEGORIES.MARKETING]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <PortalSessionProvider>
          <BookingDraftProvider>
            <Router>
              <CrispChat enabled={canLoadTracking} />
              <Routes>
                {/* Auth Routes */}
                {/* Login page removed - users now sign in via HomePage sign-in panel */}
                <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />
                
                {/* Subscription & Payment */}
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                <Route path="/checkout" element={<StripePaymentPage />} />
                <Route path="/payment" element={<StripePaymentPage />} />
                <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
                <Route path="/stripe/success" element={<StripeSuccessPage />} />
                <Route path="/book/:slug/*" element={<PublicBookingLinkPage />} />
                <Route path="/s/:slug/*" element={<PublicBookingLinkPage />} />
                
                {/* Booking Management (Public, Token-Based) */}
                <Route path="/booking/manage/:token" element={<ManageBookingPage />} />
                <Route path="/booking/resend-link" element={<ResendBookingLinkPage />} />
                <Route path="/booking" element={<HomePage />} />
                
                {/* /dashboard compatibility: redirect to role-appropriate shell */}
                <Route path="/dashboard" element={<DashboardRedirect />} />

                {/* Protected App Routes */}
                <Route path="/admin/*" element={<PrivateRoute><AdminWrapper /></PrivateRoute>} />
                <Route path="/superadmin/*" element={<PrivateRoute><SuperadminWrapper /></PrivateRoute>} />
                <Route path="/app/*" element={<PortalWrapper />} />
                
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                
                {/* DISABLED: Public registration - users must subscribe first */}
                {/* <Route path="/register" element={<RegisterPage />} /> */}
              </Routes>
              <CookieConsentManager onConsentResolved={setCookiePreferences} />
              <Toaster position="top-right"
                toastOptions={{
                  // Hide toasts during logout redirect
                  className: '',
                  duration: 3500,
                  // Custom filter to suppress when global flag is set
                  // react-hot-toast doesn't support a filter; instead,
                  // we'll no-op show calls by checking the flag globally in a small shim below
                }}
              />
              {/* Global toast suppress shim */}
              <script dangerouslySetInnerHTML={{__html: `
                (function(){
                  var origError = window.__toast_error__;
                  var origSuccess = window.__toast_success__;
                })();
              `}} />
            </Router>
          </BookingDraftProvider>
        </PortalSessionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
